import { useEditCustomerMutation } from "../../store/services/customerService";
import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    IconButton,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useAddCustomerMutation } from "../../store/services/customerService";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import { IMAGE_URL } from "../../store/services/constants";
import { Icons } from '../../assets/Svgs';
import colors from "../../styles/colors";


const labelStyle = {
    fontFamily: "PSBo",
    fontSize: 15,
    color: "#333",
    mb: 0.5,
};

const EditCustomerDialog = ({ open, onClose, data }) => {
    const words = useSelector((state) => state.authReducer.words);
    const [clientType, setClientType] = useState("Individual");
    const [formValues, setFormValues] = useState({
        idNumber: data.id_no ?? "",
        licenseNumber: data.license_no ?? "",
        fullName: data.name ?? "",
        phone: data.phone ?? "",
        email: data.email ?? "",
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [image, setImage] = useState("");
    const [editCustomer, { isLoading }] = useEditCustomerMutation(); // Use the edit mutation

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value,
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === "image/png") {
            setSelectedFile(file);
        } else {
            toast.error("Please upload a valid PNG file.");
        }
    };

    const handleSubmit = async () => {
        if (!formValues.fullName || !formValues.email || !formValues.phone || !formValues.licenseNumber) {
            toast.error("Please fill in all required fields.");
            return;
        }

        const formData = new FormData();
        formData.append("name", formValues.fullName);
        formData.append("email", formValues.email);
        formData.append("phone", formValues.phone);
        formData.append("id_no", formValues.idNumber);
        formData.append("license_no", formValues.licenseNumber);

        formData.append("type", clientType);
        if (selectedFile) {
            formData.append("image", selectedFile);
        }
        formData.append("id", data.id); // Add customer ID to the payload

        try {
            const response = await editCustomer(formData).unwrap(); // Call the edit mutation
            if (response.status === "1") {
                toast.success("Customer updated successfully!");
                onClose(); // Close the dialog on success
            } else {
                toast.error("Failed to update customer. Please try again.");
            }
        } catch (error) {
            console.error("Error updating customer:", error);
            toast.error("An error occurred. Please try again.");
        }
    };

    useEffect(() => {
        if (data) {
            setFormValues({
                idNumber: data.id_no ?? "",
                fullName: data.name ?? "",
                phone: data.phone ?? "",
                licenseNumber: data.license_no ?? "",
                email: data.email ?? "",
            });
            setImage(IMAGE_URL + data.image);
            setClientType(data.type ?? "Individual");
        }
    }, [data]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Loader open={isLoading} />

            <DialogTitle sx={{ fontFamily: "PSBo", fontSize: 18, color: "#333" }}>
                {words["Edit Customer"]}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {/* Form Fields */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} display="flex" flexDirection="row" justifyContent="space-between">
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                            <Box
                                sx={{
                                    width: "95px",
                                    height: "95px",
                                    borderRadius: "50px",
                                    border: "1px dashed #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    textAlign: "center",
                                    color: "#999",
                                    backgroundColor: "#E6E6E6",
                                    cursor: "pointer",
                                    position: "relative",
                                }}
                                onClick={() => document.getElementById("profileImageUpload").click()}
                            >
                                {selectedFile ? (
                                    <img
                                        src={URL.createObjectURL(selectedFile)}
                                        alt="Profile Preview"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                        }}
                                    />
                                ) : image ? (
                                    <img
                                        style={{
                                            width: "95px",
                                            height: "95px",
                                            borderRadius: "50px",
                                        }}
                                        src={image}
                                    />
                                ) : (
                                    <Icons.imageUplaodIcon />
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    ml: 1,
                                }}
                            >
                                <Typography sx={{ fontFamily: "PMe", fontSize: 14, color: "#999" }}>
                                    Upload the Image
                                </Typography>
                                <Typography sx={{ fontFamily: "PMe", fontSize: 14, color: "#999" }}>
                                    150x150 pixel
                                </Typography>
                            </Box>
                        </Box>
                        <input
                            type="file"
                            id="profileImageUpload"
                            accept="image/png"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                    </Grid>
                    {/* Other Fields */}
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelStyle}>{words["ID Number"]}*</Typography>
                        <TextField
                            fullWidth
                            name="idNumber"
                            value={formValues.idNumber}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelStyle}>{words["Full Name"]}*</Typography>
                        <TextField
                            fullWidth
                            name="fullName"
                            value={formValues.fullName}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelStyle}>{words["Phone"]}*</Typography>
                        <TextField
                            fullWidth
                            name="phone"
                            value={formValues.phone}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelStyle}>{words["Email"]}*</Typography>
                        <TextField
                            fullWidth
                            name="email"
                            value={formValues.email}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelStyle}>{words["License no"]}*</Typography>
                        <TextField
                            fullWidth
                            name="licenseNumber"
                            value={formValues.licenseNumber}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelStyle}>{words["Client type"]}</Typography>
                        <RadioGroup
                            row
                            value={clientType}
                            onChange={(e) => setClientType(e.target.value)}
                            sx={{ display: "flex", gap: 2 }}
                        >
                            <FormControlLabel
                                value="Individual"
                                control={<Radio />}
                                label={words["Individual"]}
                            />
                            <FormControlLabel
                                value="Company"
                                control={<Radio />}
                                label={words["Company"]}
                            />
                        </RadioGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "20px", fontSize: 12 }}>
                    {words["Cancel"]}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                        backgroundColor: colors.primary,
                        fontSize: 12,
                        color: "white",
                        borderRadius: "20px",
                        padding: "8px 26px",
                    }}
                >
                    {isLoading ? "Submitting..." : words["Submit"]}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCustomerDialog;